/**
 * Last-resort guard around a lazily loaded route.
 *
 * A `React.lazy` page fetches its chunk on first visit. A deploy renames every
 * chunk, so a tab opened before it asks for a file that is no longer there, the
 * import rejects, and with nothing to catch it React unmounts the whole app to
 * a blank page. A render-time crash inside the page ends the same way.
 *
 * Either way the answer is a reload, not a retry in place: reloading fetches
 * the new `index.html`, and with it the chunk names that exist now. The copy is
 * the generic "something went wrong" from `common`, because a stale chunk and a
 * crash look the same from here.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";

import { useT } from "@/i18n";
import { reportClientError } from "@/lib/api/client-error-report";
import { logger } from "@/lib/logger";

import { ErrorState } from "./error-state";

export interface RouteErrorBoundaryProps {
  children: ReactNode;
}

interface RouteErrorBoundaryState {
  error: Error | null;
}

export class RouteErrorBoundary extends Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  public state: RouteErrorBoundaryState = { error: null };

  public static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return { error };
  }

  public componentDidCatch(error: Error, info: ErrorInfo): void {
    logger.error("[routing] route failed to load or render", error, info.componentStack);
    // Logged as feedback for IT (8 Oct): the API never sees a crashed screen.
    reportClientError({ kind: "RENDER", message: `${error.name}: ${error.message}` });
  }

  public render(): ReactNode {
    return this.state.error === null ? this.props.children : <RouteErrorFallback />;
  }
}

/** A function component, so the copy can come from `useT`. */
function RouteErrorFallback() {
  const { t } = useT();
  return (
    <ErrorState
      title={t("state.errorTitle")}
      description={t("state.errorHint")}
      onRetry={() => window.location.reload()}
    />
  );
}
